import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from './ui/Logo';
import { Button } from './ui/Button';
import { Container } from './ui/Container';
import { ThemeToggle } from './ui/ThemeToggle';
import { cn } from '../utils/cn';

interface NavigationItem {
  name: string;
  href: string;
  external?: boolean;
}

const navigation: NavigationItem[] = [
  { name: 'Home', href: '/' },
  { name: 'Features', href: '/features' },
  { name: 'Docs', href: '/docs' },
  { name: 'About', href: '/about' },
];

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest('[data-mobile-menu]')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleSmoothScroll = (href: string) => {
    // Handle anchor links for smooth scrolling
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 dark:bg-gray-950/95 backdrop-blur-md shadow-sm border-b border-gray-200/50 dark:border-gray-800/50'
          : 'bg-transparent'
      )}
    >
      <Container>
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group"
            aria-label="LoopX Home"
          >
            <Logo
              size={64}
              variant="svg"
              animated
              className="group-hover:scale-110 transition-transform duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav id="main-navigation" className="hidden md:flex items-center space-x-8" role="navigation" aria-label="Main navigation">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleSmoothScroll(item.href)}
                className={cn(
                  'text-sm font-medium transition-colors duration-200 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-2 py-1',
                  isActiveRoute(item.href)
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300'
                )}
                aria-current={isActiveRoute(item.href) ? 'page' : undefined}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <ThemeToggle size="sm" />
            <Button
              variant="outline"
              size="sm"
              disabled
              className="opacity-60 cursor-not-allowed"
              title="Available at launch - June 12th, 2026"
              aria-label="GitHub - Available at launch"
            >
              GitHub
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled
              className="opacity-60 cursor-not-allowed"
              title="Available at launch - June 12th, 2026"
              aria-label="Install Extension - Available at launch"
            >
              Install Extension
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
            data-mobile-menu
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </Container>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div
          className="md:hidden fixed inset-0 top-16 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md z-40"
          data-mobile-menu
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <Container>
            <nav className="py-8 space-y-6" role="navigation" aria-label="Mobile navigation">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleSmoothScroll(item.href)}
                  className={cn(
                    'block text-lg font-medium transition-colors duration-200 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-2 py-1',
                    isActiveRoute(item.href)
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300'
                  )}
                  aria-current={isActiveRoute(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile CTAs */}
              <div className="pt-6 space-y-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</span>
                  <ThemeToggle size="sm" />
                </div>
                <Button
                  variant="outline"
                  size="md"
                  disabled
                  className="w-full opacity-60 cursor-not-allowed"
                  title="Available at launch - June 12th, 2026"
                  aria-label="GitHub - Available at launch"
                >
                  GitHub
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  disabled
                  className="w-full opacity-60 cursor-not-allowed"
                  title="Available at launch - June 12th, 2026"
                  aria-label="Install Extension - Available at launch"
                >
                  Install Extension
                </Button>
              </div>
            </nav>
          </Container>
        </div>
      )}
    </header>
  );
};

export default Header;